/**
 * An organisation role's rates, read the one way.
 *
 * A role carries its own cost (`rate`, per `rateTime`) and bill rate
 * (`billRate`, per `billUnit`). The organisation's "Default rate calculation"
 * (`roleDefaultRateTime`) is the unit a new role starts in, and the unit of a
 * role saved before roles had one.
 *
 * Existing roles: until 2026-09-30 a role's cost was called `cost`, and roles
 * had no `billUnit` (bill rates were always per hour). Every read goes through
 * these, so a role in either shape reads the same.
 *
 * The same helpers live in mission-x-api (api/org/role/role.utils.js) and
 * mission-x-open-api (lib/roleRates.js): a change here is a change there.
 *
 * Leaf module.
 */

export const ROLE_RATE_UNITS = ["Hourly", "Daily", "Monthly"]

/** The hours in one of each unit, as the budgets count them (getHourlyRates in utils/mf-utils.js). */
export const HOURS_PER_RATE_UNIT = {
    Hourly: 1,
    Daily: 8,
    Monthly: (40 * 52) / 12,
}

/**
 * @param orgRole {Object} An organisation role (or one with a client's rate card applied)
 * @return {number|null} What the role costs the organisation per getOrgRoleRateTime
 */
export function getOrgRoleRate(orgRole) {
    // `rate` first; a role saved before 2026-09-30 only has `cost` (never migrated)
    return orgRole?.rate ?? orgRole?.cost ?? null
}

/**
 * @param orgRole {Object}
 * @param orgData {Object}
 * @return {String} The unit the role's cost is counted per
 */
export function getOrgRoleRateTime(orgRole, orgData) {
    return orgRole?.rateTime || orgData?.roleDefaultRateTime || "Hourly"
}

/**
 * @param orgRole {Object}
 * @return {number|null} What a client is billed for the role per getOrgBillUnit
 */
export function getOrgBillRate(orgRole) {
    return orgRole?.billRate ?? null
}

/**
 * @param orgRole {Object}
 * @return {String} The unit the role's bill rate is counted per. A role saved
 *         before roles had one was billed per hour.
 */
export function getOrgBillUnit(orgRole) {
    return orgRole?.billUnit || "Hourly"
}

/**
 * The units a NEW organisation role starts in: its cost and its bill rate both
 * follow the organisation's "Default rate calculation".
 *
 * @param orgData {Object}
 * @return {{rateTime: String, billUnit: String}}
 */
export function getNewOrgRoleUnits(orgData) {
    const unit = orgData?.roleDefaultRateTime || "Hourly"

    return { rateTime: unit, billUnit: unit }
}

/**
 * The cost a person's organisation role brings to a plan item, with its own
 * unit. A role without a cost leaves the plan item's cost and unit as they are.
 *
 * @param orgRole {Object}
 * @param orgData {Object}
 * @param planItem {Object}
 * @return {{rate: number|null, rateTime: String}}
 */
export function getOrgRoleRateOrPlanItems(orgRole, orgData, planItem) {
    const rate = getOrgRoleRate(orgRole)

    // A cost of 0 is a cost; only a role with none leaves the plan item alone
    return rate !== null
        ? { rate, rateTime: getOrgRoleRateTime(orgRole, orgData) }
        : { rate: planItem?.rate ?? null, rateTime: planItem?.rateTime }
}

/**
 * A client's rate card only holds what differs from the organisation's roles:
 * an entry per role that has a client price, with the cost pair (`rate` +
 * `rateTime`) and/or the bill pair (`billRate` + `billUnit`). A pair the entry
 * doesn't have is read from the org role, so it follows the role's changes.
 *
 * Cards made before 2026-09-30 copied every role with all four fields. Those
 * still read as they always have (every field they hold wins), so nothing
 * priced from them changes until they are cleaned up.
 */
const RATE_CARD_PAIRS = [
    ["rate", "rateTime"],
    ["billRate", "billUnit"],
]

const isSet = (value) => value !== undefined && value !== null && value !== ""

/**
 * @param orgRole {Object}
 * @param entry {Object|undefined} The role's entry in a client's rate card
 * @return {Object} The role with the entry's own fields over it
 */
export function withRateCardEntry(orgRole, entry) {
    if (!orgRole || !entry) {
        return orgRole
    }

    const role = { ...orgRole }

    RATE_CARD_PAIRS.flat().forEach((key) => {
        if (isSet(entry[key])) {
            role[key] = entry[key]
        }
    })

    return role
}

/**
 * The organisation's rate cards after one role's client price changed.
 *
 * `change` is a whole pair: `{ rate, rateTime }` or `{ billRate, billUnit }`.
 * A price with its unit is kept together, so a client's 60 per hour never
 * turns into 60 per month when the org role's unit changes. A pair set back to
 * the org role's own price, or cleared, is dropped; an entry left with nothing
 * is dropped, and so is a card left with no entries.
 *
 * @param rateCards {Object[]|undefined}
 * @param usedForId {String} The client
 * @param orgRole {Object}
 * @param orgData {Object}
 * @param change {Object}
 * @return {Object[]}
 */
export function withRateCardChange(rateCards, usedForId, orgRole, orgData, change) {
    const cards = (rateCards || []).slice()
    const cardIndex = cards.findIndex((rc) => rc.usedForId === usedForId)
    const card =
        cardIndex === -1 ? { usedFor: "client", usedForId, ratesByRole: [] } : { ...cards[cardIndex] }
    const entries = (card.ratesByRole || []).slice()
    const entryIndex = entries.findIndex((e) => e.roleId === orgRole._id)
    const entry = entryIndex === -1 ? { roleId: orgRole._id } : { ...entries[entryIndex] }

    const orgValues = {
        rate: getOrgRoleRate(orgRole),
        rateTime: getOrgRoleRateTime(orgRole, orgData),
        billRate: getOrgBillRate(orgRole),
        billUnit: getOrgBillUnit(orgRole),
    }

    RATE_CARD_PAIRS.forEach(([amount, unit]) => {
        if (!(amount in change) && !(unit in change)) {
            return
        }

        const value = (amount in change) ? change[amount] : entry[amount]
        const valueUnit = change[unit] || entry[unit] || orgValues[unit]
        // A client price of 0 on a role with no price of its own is a price, not the org's
        const sameAsOrg =
            isSet(value) &&
            isSet(orgValues[amount]) &&
            +value === +orgValues[amount] &&
            valueUnit === orgValues[unit]

        if (!isSet(value) || sameAsOrg) {
            delete entry[amount]
            delete entry[unit]
        } else {
            entry[amount] = +value
            entry[unit] = valueUnit
        }
    })

    const hasPrice = RATE_CARD_PAIRS.flat().some((key) => isSet(entry[key]))

    if (entryIndex === -1) {
        if (hasPrice) entries.push(entry)
    } else if (hasPrice) {
        entries[entryIndex] = entry
    } else {
        entries.splice(entryIndex, 1)
    }

    card.ratesByRole = entries

    if (cardIndex === -1) {
        if (entries.length) cards.push(card)
    } else if (entries.length) {
        cards[cardIndex] = card
    } else {
        cards.splice(cardIndex, 1)
    }

    return cards
}

/**
 * The organisation's rate cards without entries for roles that no longer
 * exist (archived or gone), and without cards left empty by that.
 *
 * @param rateCards {Object[]|undefined}
 * @param roles {Object[]|undefined} The organisation's roles
 * @param isArchived {Function}
 * @return {Object[]}
 */
export function withoutRemovedRoles(rateCards, roles, isArchived) {
    const live = new Set((roles || []).filter((r) => !isArchived(r)).map((r) => r._id))

    return (rateCards || [])
        .map((rc) => ({ ...rc, ratesByRole: (rc.ratesByRole || []).filter((e) => live.has(e.roleId)) }))
        .filter((rc) => rc.ratesByRole.length)
}

const toNumber = (value) => +value || 0

/**
 * Whether a plan item's cost or bill rate is the one its client's rate card sets
 * for the role: the card holds a price of its own for it (not the org role's),
 * and the plan item carries that price and unit. GT/GR projects don't use rate
 * cards; the caller leaves them out.
 *
 * @param type {"cost"|"bill"}
 * @return {boolean}
 */
export function isRateFromRateCard({ pi, orgData, mission, type }) {
    if (!pi?.role || !mission?.client) {
        return false
    }

    const orgRole = orgData?.roles?.find((r) => r._id === pi.role)
    const entry = orgData?.rateCards
        ?.find((rc) => rc.usedForId === mission.client)
        ?.ratesByRole?.find((e) => e.roleId === pi.role)

    if (!orgRole || !entry) {
        return false
    }

    const [amount, unit, orgAmount, orgUnit] =
        type === "bill"
            ? ["billRate", "billUnit", getOrgBillRate(orgRole), getOrgBillUnit(orgRole)]
            : ["rate", "rateTime", getOrgRoleRate(orgRole), getOrgRoleRateTime(orgRole, orgData)]

    if (entry[amount] === undefined || entry[amount] === null) {
        return false
    }

    const cardUnit = entry[unit] || orgUnit
    const cardIsOwn = toNumber(entry[amount]) !== toNumber(orgAmount) || cardUnit !== orgUnit

    return cardIsOwn && toNumber(pi[amount]) === toNumber(entry[amount]) && (pi[unit] || orgUnit) === cardUnit
}

/** @deprecated Use getOrgRoleRateTime */
export function roleRateTime(role, orgData) {
    return getOrgRoleRateTime(role, orgData)
}

/** @deprecated Use getOrgBillUnit */
export function roleBillTime(role) {
    return getOrgBillUnit(role)
}

/**
 * A cost per one unit as a cost per another, to the cent.
 *
 * @param cost {number|null}
 * @param from {String} One of ROLE_RATE_UNITS
 * @param to {String} One of ROLE_RATE_UNITS
 * @return {number|null} null for no cost
 */
export function convertRoleCost(cost, from, to) {
    const value = Number(cost)

    if (cost === null || cost === undefined || cost === "" || !Number.isFinite(value)) {
        return null
    }

    const hoursFrom = HOURS_PER_RATE_UNIT[from]
    const hoursTo = HOURS_PER_RATE_UNIT[to]

    if (!hoursFrom || !hoursTo || from === to) {
        return value
    }

    return Math.round((value / hoursFrom) * hoursTo * 100) / 100
}
